<template>
    <section class="relative pt-4 pb-8 min-h-screen bg-center bg-cover md:pt-6 md:pb-20">
        <!-- Background Image -->
        <div class="absolute inset-0 z-0 md:bg-[#253229] bg-[#183031]">
            <nuxt-img :src="backgroundImage" :alt="backgroundImageAlt" class="object-cover object-top w-full h-full"
                :class="mobileBackgroundImage ? 'hidden md:block' : 'block'" fetchpriority="high" loading="eager"
                sizes="100vw sm:100vw md:100vw lg:100vw xl:100vw" format="webp" width="1920" height="1080" />
            <nuxt-img v-if="mobileBackgroundImage" :src="mobileBackgroundImage" :alt="backgroundImageAlt"
                class="object-cover object-top w-full md:hidden block" fetchpriority="high" loading="eager"
                sizes="100vw sm:100vw md:100vw lg:100vw xl:100vw" format="webp" width="1920" height="1080" />
        </div>

        <!-- Overlay to ensure text readability -->
        <div class="absolute inset-0 z-0 bg-black/10"></div>

        <div class="relative z-10  mx-auto max-w-7xl sm:px-6 md:mt-0 mt-8">
            <!-- Top Header (Logo & Mini CTA) -->
            <div class="flex md:justify-between justify-center items-center mb-10 md:mb-8">
                <div>
                    <a href="https://lifeboostcoffee.com/" aria-label="Lifeboost Coffee">
                        <!-- Lifeboost Text Logo SVG -->
                        <svg class="md:w-auto" width="163" height="60" viewBox="0 0 163 60" fill="none"
                            xmlns="http://www.w3.org/2000/svg" aria-label="Lifeboost Coffee Logo" role="img">
                            <title>Lifeboost Coffee Logo</title>
                            <path d="M46.2736 20.3906H43.8984V40.4763H46.2736V20.3906Z" fill="white" />
                            <path d="M32.4025 20.3906H30.0273V40.4763H40.3808V39.3435H32.4025V20.3906Z" fill="white" />
                            <path
                                d="M85.1029 29.8184C86.2966 29.2459 87.4537 28.6612 88.2333 27.5406C89.6706 25.4577 89.1956 22.3882 86.8691 21.2189C85.2612 20.415 83.4463 20.3906 81.6924 20.3906H76.5156V40.4763C80.1211 40.4763 84.4086 41.1219 87.5877 39.0634C89.9751 37.5165 90.5111 33.7283 88.5865 31.5845C87.673 30.5614 86.3697 30.1716 85.1029 29.8184ZM78.8908 21.5356C79.9993 21.5356 81.0955 21.5843 82.2039 21.633C83.2636 21.6818 84.3843 21.6939 85.31 22.3151C87.3198 23.6672 86.7229 27.1508 84.8958 28.3567C83.1662 29.5017 80.8641 29.2946 78.8908 29.2946V21.5356ZM85.8215 38.3447C83.9701 39.6724 81.0346 39.3435 78.8908 39.3435V30.5492C80.9859 30.5492 83.4342 30.2447 85.31 31.3896C87.5025 32.7295 88.0506 36.7491 85.8215 38.3447Z"
                                fill="white" />
                            <path
                                d="M63.2539 40.4763H73.5099V39.3435H65.6413V30.6466H72.6816V29.4042H65.6413V21.5356H73.5099V20.3906H63.2539V40.4763Z"
                                fill="white" />
                            <path
                                d="M50.3086 40.5818H52.6838V30.7521H59.7241V29.5097H52.6838V21.6411H60.5524V20.4961H50.3086V40.5818Z"
                                fill="white" />
                            <path
                                d="M31.8384 12.6348C31.8384 12.2328 31.4852 12.0745 31.1563 11.977C30.4498 11.7699 29.7068 11.636 28.9882 11.502C27.9163 11.2827 26.8566 11.0757 25.7847 10.8929C25.2853 10.8077 24.591 10.7955 24.1525 10.5397C23.6409 10.2474 23.4095 9.38256 23.0441 8.9197C22.1792 7.82345 20.9003 7.1048 19.4752 7.1657C18.1475 7.21442 16.832 7.77473 15.7479 8.52992C15.3825 8.78571 14.4081 9.50436 15.1511 9.95504C15.7601 10.3205 16.7833 9.23639 17.307 8.95624C18.8296 8.14014 20.5714 7.9818 21.8504 9.34602C22.2158 9.73579 22.5203 10.2108 22.7639 10.6859C22.9101 10.9538 22.9953 11.3314 23.2511 11.5142C23.6531 11.8065 24.3839 11.7821 24.859 11.8674C26.1136 12.0988 27.4169 12.4886 28.6837 12.5495C26.9418 13.0733 24.9808 12.9271 23.2024 13.2194C22.362 13.3534 21.6311 13.865 20.8272 14.0964C20.072 14.3157 19.2437 14.4131 18.72 13.6823C18.2449 13.0002 18.4155 11.3071 19.5726 11.5629C20.1329 11.6847 20.2182 12.5251 20.815 12.513C21.1926 12.5008 21.3388 12.1597 21.2901 11.8308C21.2292 11.3314 20.8394 10.8686 20.4009 10.6372C20.1451 10.5032 19.8528 10.4301 19.5726 10.4057C16.7955 10.1743 16.5275 14.6324 18.9514 15.2779C20.5592 15.7043 21.8382 14.7785 23.2999 14.3766C23.7993 14.2304 24.3474 14.2548 24.8468 14.1939C26.2232 14.0233 27.6118 13.8894 28.9882 13.7066C29.7068 13.6092 30.462 13.5483 31.1563 13.3412C31.4852 13.2072 31.8384 13.0245 31.8384 12.6348Z"
                                fill="white" />
                            <path d="M139.566 20.3906V21.5356H145.766V40.4763H148.154V21.5356H154.256V20.3906H139.566Z"
                                fill="white" />
                            <path
                                d="M129.284 26.505C127.652 24.1663 129.552 21.4135 132.098 21.0481C133.28 20.8776 134.595 21.0603 135.716 21.4379C136.313 21.6449 136.873 21.9738 137.47 22.1565C137.47 21.8155 137.543 21.3404 137.36 21.0359C137.19 20.7558 136.824 20.6461 136.532 20.5243C135.691 20.1711 134.839 19.9762 133.95 19.8422C130.965 19.4159 127.53 20.9263 126.921 24.1176C126.8 24.7875 126.751 25.5062 126.848 26.1883C127.275 29.1603 129.881 30.3296 132.39 31.3406C134.047 32.0106 135.813 32.7292 136.605 34.471C137.713 36.9315 136.008 39.3554 133.535 39.9157C131.051 40.476 128.834 39.6965 126.702 38.515C126.702 38.8926 126.617 39.4041 126.812 39.733C126.994 40.0497 127.421 40.2324 127.738 40.3786C128.529 40.744 129.358 40.9876 130.222 41.146C133.353 41.6941 137.336 40.6953 138.542 37.3822C138.797 36.6879 138.931 35.9449 138.919 35.214C138.907 34.6172 138.81 34.0204 138.639 33.46C137.36 29.0994 131.623 29.8424 129.284 26.505Z"
                                fill="white" />
                            <path
                                d="M22.5462 16.7726C22.4 16.4194 22.0346 16.1514 21.6692 16.4315C21.1941 16.8091 21.657 17.8445 21.7666 18.3195C22.132 20.037 22.4609 21.8397 22.4609 23.5937C22.4609 27.4549 20.9383 31.2796 17.6009 33.4234C16.1879 34.3369 14.6532 34.7754 13.0453 35.1896C16.8091 28.8191 17.9663 20.9627 14.9212 14.0685C13.3499 10.5118 10.9016 7.3814 8.07569 4.73823C6.90636 3.65416 5.71267 2.63099 4.45807 1.64437C3.70288 1.07189 2.93551 0.414138 2.07069 0C0.621207 2.3752 0 5.31071 0 8.07569C0 13.496 2.04633 18.4535 5.42034 22.668C6.8211 24.422 8.35584 26.0298 10.0368 27.5036C10.4387 27.8569 10.8407 28.2467 11.2792 28.5512C11.4497 28.673 11.6446 28.807 11.8638 28.6852C12.1318 28.5268 12.2414 28.1005 12.3389 27.8447C12.6921 26.9433 12.8505 26.0054 12.9723 25.0554C13.5448 20.6095 11.5106 16.2488 8.83089 12.8383C8.01479 11.8029 7.11343 10.731 6.10245 9.87841C5.79793 9.62262 5.23763 9.08668 4.83567 9.50081C4.40935 9.92713 4.99402 10.4753 5.27417 10.7676C6.23643 11.742 7.19869 12.6921 8.05133 13.764C10.999 17.5034 12.8992 22.4 11.3888 27.1139C9.05013 24.7874 6.69929 22.6192 4.83567 19.8664C2.54573 16.4803 0.852637 12.1318 1.03535 7.96607C1.13279 5.77357 1.65655 3.66634 2.48483 1.65655C4.75041 3.45927 7.04035 5.20109 8.98923 7.34486C13.5326 12.3632 16.395 18.6119 15.384 25.4573C15.1039 27.3575 14.5436 29.282 13.7884 31.0482C12.9114 33.1189 11.8638 35.2017 10.5605 37.0532C8.90397 39.404 7.02817 41.4869 5.59086 43.9839C4.93312 45.1289 4.43371 46.4078 4.20228 47.7111C4.1292 48.1131 3.99521 48.8683 4.25101 49.2337C4.47026 49.5504 4.96966 49.4895 5.11582 49.1484C5.3838 48.5272 5.32289 47.6624 5.54214 46.9925C6.005 45.5674 6.76019 44.2032 7.58847 42.9607C8.67254 41.3285 9.92713 39.8547 11.1086 38.3078C11.5106 37.784 11.9004 36.7974 12.4607 36.4442C12.7896 36.2371 13.3864 36.2493 13.764 36.1762C14.5801 36.0178 15.3718 35.7864 16.1392 35.4697C19.4767 34.142 21.9371 31.5232 22.9238 28.0518C23.7399 25.1893 23.6424 22.0468 23.0699 19.1478C22.9359 18.3804 22.8385 17.5156 22.5462 16.7726Z"
                                fill="white" />
                            <path
                                d="M124.497 24.8478C123.096 21.3886 119.563 19.2813 115.848 19.8173C115.032 19.9391 114.216 20.1218 113.473 20.475C109.234 22.4605 108.199 27.4301 108.491 31.6811C108.589 33.0941 108.747 34.4948 109.271 35.8225C110.647 39.2939 113.985 41.7544 117.822 41.2794C118.601 41.1819 119.369 40.987 120.099 40.6825C124.679 38.7824 125.849 33.3377 125.398 28.8796C125.252 27.5032 125.02 26.1512 124.497 24.8478ZM119.685 39.0991C119.064 39.4036 118.394 39.5254 117.724 39.6106C115.093 39.9639 112.755 38.6727 111.707 36.2366C111.195 35.0308 110.976 33.7031 110.879 32.3998C110.623 28.8918 110.745 23.8125 114.411 21.9854C114.984 21.6931 115.641 21.5591 116.275 21.4738C119.076 21.0719 121.5 22.8746 122.377 25.469C122.706 26.4191 122.913 27.3692 123.011 28.368C123.071 29.0623 123.108 29.7322 123.108 30.4387C123.096 33.4595 122.779 37.5643 119.685 39.0991Z"
                                fill="white" />
                            <path
                                d="M106.542 25.3598C105.239 21.6813 101.804 19.2452 97.8331 19.8177C97.017 19.9395 96.201 20.1222 95.4579 20.4754C91.1704 22.4852 90.1594 27.8081 90.5735 32.0956C90.6953 33.3624 90.8659 34.6292 91.3409 35.8229C92.693 39.2578 95.9817 41.7913 99.8064 41.2676C100.562 41.1701 101.28 40.9996 101.987 40.7073C106.323 38.8558 107.565 33.9227 107.273 29.6108C107.163 28.1491 107.042 26.7606 106.542 25.3598ZM101.975 38.8802C101.244 39.3309 100.428 39.5014 99.5993 39.611C96.6029 40.0252 94.2155 38.1737 93.2898 35.4087C92.9975 34.5317 92.8513 33.6304 92.7539 32.7168C92.3763 29.1723 92.6564 23.7763 96.3958 21.998C96.9927 21.7178 97.6139 21.5717 98.2595 21.4742C101.171 21.0601 103.461 23.0333 104.338 25.6765C104.654 26.6266 104.8 27.5767 104.91 28.5755C104.983 29.2698 104.995 29.9519 104.995 30.6462C104.983 33.4599 104.642 37.248 101.975 38.8802Z"
                                fill="white" />
                            <path d="M157.338 20.9747H156.266V20.3535H159.104V20.9747H158.032V23.7153H157.338V20.9747Z"
                                fill="white" />
                            <path
                                d="M163 20.293V23.7157H162.318V21.9252L161.465 22.9849H161.246L160.405 21.9252V23.7157H159.711V20.293H159.942L161.355 22.1079L162.78 20.293H163Z"
                                fill="white" />
                            <path
                                d="M72.4004 53.3128C73.4114 53.3128 74.264 53.8 74.7756 54.5674L74.13 55.0912C73.6671 54.47 73.0825 54.1533 72.4126 54.1533C71.2189 54.1533 70.3175 55.1277 70.3175 56.3214C70.3175 57.5273 71.2189 58.5017 72.4126 58.5017C73.0825 58.5017 73.655 58.1972 74.1178 57.576L74.7634 58.0876C74.2396 58.855 73.3992 59.3422 72.4004 59.3422C70.7316 59.3422 69.3796 57.9901 69.3796 56.3214C69.3431 54.6649 70.756 53.2885 72.4004 53.3128Z"
                                fill="white" />
                            <path
                                d="M80.0527 53.3132C81.6971 53.2888 83.11 54.6774 83.0735 56.3218C83.11 57.9418 81.6849 59.3669 80.0405 59.3304C78.3962 59.3669 77.0076 57.9418 77.0319 56.3218C76.9954 54.6652 78.3962 53.2767 80.0527 53.3132ZM80.0527 54.1537C78.8712 54.1537 77.9698 55.0916 77.9698 56.334C77.9698 57.5398 78.8712 58.4899 80.0405 58.4899C81.2099 58.4899 82.1356 57.5398 82.1356 56.334C82.1356 55.0794 81.2221 54.1537 80.0527 54.1537Z"
                                fill="white" />
                            <path
                                d="M86.9567 53.4358H88.4061V54.2519H86.9567V59.1972H86.0431V54.2519H85.2148V53.4358H86.0431V52.0594C86.0431 50.7926 86.774 50.1836 87.9433 50.1836C88.1869 50.1836 88.4427 50.2201 88.6619 50.281V51.0484C88.4671 51.0241 88.3087 51.0119 88.1504 51.0119C87.383 51.0119 86.9445 51.292 86.9445 52.1081V53.4358H86.9567Z"
                                fill="white" />
                            <path
                                d="M91.4254 53.4358H92.8749V54.2519H91.4254V59.1972H90.5119V54.2519H89.6836V53.4358H90.5119V52.0594C90.5119 50.7926 91.2427 50.1836 92.412 50.1836C92.6556 50.1836 92.9114 50.2201 93.1307 50.281V51.0484C92.9358 51.0241 92.7775 51.0119 92.6191 51.0119C91.8517 51.0119 91.4132 51.292 91.4132 52.1081V53.4358H91.4254Z"
                                fill="white" />
                            <path
                                d="M95.7426 56.6012C95.8522 57.734 96.7292 58.4892 97.862 58.4892C98.5076 58.4892 99.1044 58.2334 99.6282 57.7097L100.164 58.3309C99.5551 58.9521 98.7634 59.3419 97.862 59.3419C96.1446 59.3419 94.8047 58.0264 94.8047 56.3333C94.8047 54.6645 96.108 53.3125 97.7524 53.3125C99.2384 53.3125 100.383 54.3844 100.383 55.9191C100.383 56.1384 100.359 56.3698 100.31 56.6012H95.7426ZM99.4333 55.7973C99.4211 54.762 98.6781 54.1408 97.7158 54.1408C96.7536 54.1408 95.974 54.7985 95.7791 55.7973H99.4333Z"
                                fill="white" />
                            <path
                                d="M103.743 56.6012C103.853 57.734 104.73 58.4892 105.862 58.4892C106.508 58.4892 107.105 58.2334 107.629 57.7097L108.165 58.3309C107.556 58.9521 106.764 59.3419 105.85 59.3419C104.133 59.3419 102.793 58.0264 102.793 56.3333C102.793 54.6645 104.096 53.3125 105.741 53.3125C107.227 53.3125 108.372 54.3844 108.372 55.9191C108.372 56.1384 108.347 56.3698 108.299 56.6012H103.743ZM107.434 55.7973C107.422 54.762 106.679 54.1408 105.716 54.1408C104.754 54.1408 103.974 54.7985 103.78 55.7973H107.434Z"
                                fill="white" />
                        </svg>
                    </a>
                </div>
                <button v-if="showTopButton" @click="scrollTo(topButtonLink)" :aria-label="topButtonText"
                    class="gap-2 items-center md:flex hidden px-6 py-2 sm:py-2.5 text-xs sm:text-sm md:text-base font-semibold text-white uppercase rounded-sm border-b-2 border-[#b54f15] transition-colors bg-[#E37A33] hover:bg-[#D16922] shadow-md font-ww-medium">
                    {{ topButtonText }}
                    <span>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"
                            aria-hidden="true">
                            <path
                                d="M0.000273357 7.93934L10.5559 7.93934L6.5581 12.6702L8.04351 13.9294L13.9297 6.96471L8.04351 -2.57293e-07L6.5581 1.25922L10.5559 5.99008L0.000273443 5.99008L0.000273357 7.93934Z"
                                fill="white" />
                        </svg>
                    </span>
                </button>
            </div>

            <div v-if="bannerText" class="mx-auto md:mb-8 mb-4 max-w-4xl sm:px-2 md:px-0 text-center">
                <h2 style="text-shadow: 0px 2px 6.9px #000000;"
                    class="text-[2.2rem] md:text-4xl font-ww-medium font-bold leading-none uppercase tracking-tight text-white text-balance max-w-[360px] md:max-w-none mx-auto">
                    <span v-html="bannerText.replace(/\s?\([^)]+\)/g, m => m.replace(/\s/g, '&nbsp;'))"
                        class="drop-shadow-[0_2px_4px_rgba(0,0,0,0.4)] px-3 md:px-4 py-1 md:py-[0.35rem] leading-[1.3] md:leading-[1.4]"
                        style="background: linear-gradient(0deg, rgb(101 15 10 / 88%), rgb(92 5 0 / 70%)); mix-blend-mode: multiply; display: inline; -webkit-box-decoration-break: clone; box-decoration-break: clone; box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.2);"></span>
                </h2>
            </div>

            <!-- Hero Main Section: 2 Columns on Desktop -->
            <div
                class="grid grid-cols-1 md:gap-12 items-center sm:px-0 px-4 md:grid-cols-12 mb-10 mx-auto w-full max-w-4xl xl:relative xl:-right-4">
                <!-- Left: Typography -->
                <div
                    class="col-span-12 lg:col-span-7 text-center md:text-left text-white px-2 md:px-0 lg:order-1 order-2">
                    <h1 class="mb-5 text-4xl md:text-4xl lg:text-left text-center lg:text-40 font-bold !leading-[44px] font-ww-bold drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)] [text-shadow:0_4px_10px_rgba(0,0,0,0.5)]"
                        :class="mobileMainHeadline ? 'hidden md:block' : 'block'" v-html="mainHeadline"></h1>
                    <h1 v-if="mobileMainHeadline"
                        class="mb-5 text-4xl md:text-4xl lg:text-left text-center lg:text-40 font-bold !leading-[44px] font-ww-bold drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)] [text-shadow:0_4px_10px_rgba(0,0,0,0.5)] md:hidden block"
                        v-html="mobileMainHeadline"></h1>
                    <div class="lg:text-left text-center text-[22px] md:px-0 px-2 font-ww-medium leading-8 text-white drop-shadow-[0_2px_6px_rgba(0,0,0,0.8)] text-balance"
                        v-html="subHeadline"></div>
                </div>

                <!-- Right: Product Specifics -->
                <div
                    class="col-span-12 lg:col-span-5 flex-col flex justify-center relative md:mt-0 md:px-0 lg:order-2 order-1">
                    <nuxt-img v-if="productImage" :src="productImage" :alt="productImageAlt"
                        class="h-auto xl:max-w-none w-full drop-shadow-[0_20px_40px_rgba(0,0,0,0.6)] lg:max-w-auto sm:max-w-lg max-w-[411px] mx-auto xl:min-w-fit"
                        width="800" height="800" sizes="90vw sm:80vw md:50vw lg:450px xl:600px" format="webp"
                        fetchpriority="high" loading="eager" preload />

                    <div class="justify-center mb-4 md:mb-10 w-full px-4 md:hidden flex">
                        <button @click="scrollTo(ctaLink)" :aria-label="ctaText"
                            class="flex justify-center items-center gap-3 w-full md:w-auto min-w-[280px] md:min-w-[530px] px-8 py-5 md:py-5 text-3xl lg:text-3xl font-bold text-white uppercase bg-[#E37A33] hover:bg-[#D16922] border-b-4 border-[#b54f15] rounded shadow-2xl transition-transform font-ww-medium">
                            {{ ctaText }}
                            <span>
                                <svg width="23" height="23" viewBox="0 0 23 23" fill="none"
                                    xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                    <path
                                        d="M-0.000595652 12.8002L17.0176 12.8002L10.5722 20.4275L12.9671 22.4576L22.457 11.2288L12.9671 -4.14819e-07L10.5722 2.03017L17.0176 9.65747L-0.000595515 9.65747L-0.000595652 12.8002Z"
                                        fill="white" />
                                </svg>
                            </span>
                        </button>
                    </div>
                </div>
            </div>

            <div class="mx-auto max-w-5xl text-center">
                <!-- Main CTA Button -->
                <div class="justify-center mb-8 md:mb-10 w-full px-4 md:flex hidden">
                    <button @click="scrollTo(ctaLink)" :aria-label="ctaText"
                        class="flex justify-center items-center gap-3 w-full md:w-auto min-w-[280px] md:min-w-[530px] px-8 py-4 md:py-5 text-3xl lg:text-3xl font-bold text-white uppercase bg-[#E37A33] hover:bg-[#D16922] border-b-4 border-[#b54f15] rounded shadow-2xl transition-transform font-ww-medium">
                        {{ ctaText }}
                        <span>
                            <svg width="23" height="23" viewBox="0 0 23 23" fill="none"
                                xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                <path
                                    d="M-0.000595652 12.8002L17.0176 12.8002L10.5722 20.4275L12.9671 22.4576L22.457 11.2288L12.9671 -4.14819e-07L10.5722 2.03017L17.0176 9.65747L-0.000595515 9.65747L-0.000595652 12.8002Z"
                                    fill="white" />
                            </svg>
                        </span>
                    </button>
                </div>

                <!-- Informational Text -->
                <div class="mb-12 text-xl lg:text-xl leading-relaxed max-w-3xl mx-auto text-white drop-shadow-md font-ww-regular text-center [&>p]:mb-4 px-2 md:px-6"
                    v-html="infoText">
                </div>

                <svg class="mx-auto lg:mb-10 mb-6 w-full" width="672" height="17" viewBox="0 0 672 17" fill="none"
                    xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <g filter="url(#filter0_dd_4118_101)">
                        <path
                            d="M7 8.5C17.9667 8.6 28.9333 8.695 39.9 8.785C138.6 9.595 237.3 10 336 10C434.7 10 533.4 9.595 632.1 8.785C643.067 8.695 654.033 8.6 665 8.5C654.033 8.4 643.067 8.305 632.1 8.215C533.4 7.405 434.7 7 336 7C237.3 7 138.6 7.405 39.9 8.215C28.9333 8.305 17.9667 8.4 7 8.5Z"
                            fill="#F87832" />
                    </g>
                    <defs>
                        <filter id="filter0_dd_4118_101" x="0" y="0" width="672" height="17"
                            filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
                            <feFlood flood-opacity="0" result="BackgroundImageFix" />
                            <feColorMatrix in="SourceAlpha" type="matrix"
                                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                            <feOffset />
                            <feGaussianBlur stdDeviation="3.5" />
                            <feComposite in2="hardAlpha" operator="out" />
                            <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 0.816667 0 0 0 0 0 0 0 0 1 0" />
                            <feBlend mode="overlay" in2="BackgroundImageFix" result="effect1_dropShadow_4118_101" />
                            <feColorMatrix in="SourceAlpha" type="matrix"
                                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                            <feOffset />
                            <feComposite in2="hardAlpha" operator="out" />
                            <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 0.8 0 0 0 0 0 0 0 0 1 0" />
                            <feBlend mode="normal" in2="effect1_dropShadow_4118_101"
                                result="effect2_dropShadow_4118_101" />
                            <feBlend mode="normal" in="SourceGraphic" in2="effect2_dropShadow_4118_101"
                                result="shape" />
                        </filter>
                    </defs>
                </svg>

                <!-- Subtle CSS Divider -->


                <!-- Features & Benefits Section -->
                <div class="mx-auto max-w-5xl">
                    <div class="mb-10 text-3xl md:text-3xl lg:text-4xl font-bold text-center text-white drop-shadow-lg font-ww-medium leading-tight"
                        v-html="featuresTitle"></div>

                    <!-- 4 Icon Features Row -->
                    <div v-if="features && features.length > 0"
                        class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 md:gap-8 mb-6 md:mb-12 px-6">
                        <div v-for="(feature, index) in features" :key="'feat-' + index"
                            class="flex flex-col items-center text-center md:px-0 px-12">
                            <div class="mb-7">
                                <div v-if="feature.icon && feature.icon.startsWith('<svg')" v-html="feature.icon"
                                    class="" />
                                <nuxt-img v-else-if="feature.icon" :src="feature.icon" :alt="feature.title"
                                    class="w-24 h-24 md:w-20 md:h-20" width="80" height="80" loading="lazy"
                                    sizes="64px md:80px" format="webp" />
                                <svg v-else class="" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.5 17.52 2 12 2ZM11 19.93C7.05 19.43 4.07 16.05 4.07 12C4.07 11.66 4.1 11.33 4.14 11H11V19.93ZM13 19.93V11H19.85C19.89 11.33 19.92 11.66 19.92 12C19.92 16.05 16.94 19.43 13 19.93ZM19.74 9H13V4.11C16.5 4.71 19.16 7.55 19.74 9ZM11 4.11V9H4.25C4.83 7.54 7.49 4.7 11 4.11Z" />
                                </svg>
                            </div>
                            <h3 class="mb-2 text-[#FAF9F5] text-xl font-bold drop-shadow-md font-ww-bold"
                                v-html="feature.title"></h3>
                            <p class="text-xl text-gray-200 drop-shadow-md font-ww-regular leading-snug"
                                v-html="feature.description"></p>
                        </div>
                    </div>

                    <!-- Bullet Points (Checkmarks) List -->
                    <div class="max-w-3xl md:p-8 mx-auto p-6 px-4 rounded-xl text-left">
                        <ul class="space-y-4 md:space-y-5">
                            <li v-for="(benefit, index) in benefits" :key="'ben-' + index"
                                class="flex gap-4 items-start">
                                <!-- Enhanced Checkmark Icon -->
                                <div class="flex-shrink-0 mt-1">
                                    <div
                                        class="flex justify-center items-center w-6 h-6 bg-[#75B340] rounded-full shadow-md">
                                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none"
                                            xmlns="http://www.w3.org/2000/svg">
                                            <path d="M2.5 6L5 8.5L9.5 3" stroke="white" stroke-width="2.5"
                                                stroke-linecap="round" stroke-linejoin="round" />
                                        </svg>
                                    </div>
                                </div>
                                <div
                                    class="text-xl text-white drop-shadow-sm md:text-lg lg:text-xl font-ww-regular leading-relaxed md:leading-tight">
                                    <span class="font-bold text-white font-ww-bold" v-if="benefit.boldText">{{
                                        benefit.boldText }}</span>
                                    <span v-if="benefit.boldText && benefit.text" class="text-white/90"> — </span>
                                    <span v-html="benefit.text" class="text-white"></span>
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>
<script setup>
import { scrollTo } from '@/composables/scrollTo';

const props = defineProps({
    backgroundImage: {
        type: String,
        default: '/assets/images/bogo-new-header-2.jpg',
    },
    mobileBackgroundImage: {
        type: String,
        default: '/assets/images/bogo-new-mobile-crop-new.jpg',
    },
    backgroundImageAlt: {
        type: String,
        default: 'Promotional offer background',
    },
    productImage: {
        type: String,
        default: '/assets/images/bogo-new-6.png',
    },
    productImageAlt: {
        type: String,
        default: 'Promotional Offer Products',
    },
    bannerText: {
        type: String,
        default: 'BUY 2, GET 1 FREE... BUY 4, GET 2 FREE ( 58% OFF )',
    },
    mainHeadline: {
        type: String,
        default: 'Stock Up on Low Acid Coffee That\'s Easy On Your Stomach - Third-Party Verified: Mycotoxins Tested & Cleared',
    },
    mobileMainHeadline: {
        type: String,
        default: '',
    },
    subHeadline: {
        type: String,
        default: 'For anyone who loves coffee but hates how it makes their stomach feel — or worries what\'s hiding in their beans — this is the cleanest, simplest upgrade you can make.',
    },
    ctaText: {
        type: String,
        default: 'ADD TO CART',
    },
    ctaLink: {
        type: String,
        default: 'the-products',
    },
    infoText: {
        type: String,
        default: '<p>Every Lifeboost bag is single-origin, organic, naturally low-acid, and screened for 450+ unwanted compounds so you can enjoy your daily cup without second-guessing what\'s in it.</p><p>Buying in bulk saves you money, reduces packaging waste, cuts shipping emissions, and keeps your clean coffee shelf stocked for weeks.</p>',
    },
    featuresTitle: {
        type: String,
        default: 'Why People Switch to Lifeboost<br>(and Stay for Good):',
    },
    features: {
        type: Array,
        default: () => [
            { icon: '/assets/images/feature-low-acid.svg', title: 'Naturally low-acid & easy to drink', description: 'Gentle on taste, teeth, and sensitive stomachs.' },
            { icon: '/assets/images/feature-chemical.svg', title: 'Chemical-conscious sourcing', description: 'Organic farming, no pesticides, no flavor additives.' },
            { icon: '/assets/images/feature-toxins.svg', title: '450+ toxins tested (Including Mycotoxins) on every batch', description: 'Independent screening for purity and safety.' },
            { icon: '/assets/images/feature-single-origin.svg', title: 'Single-origin', description: 'No blends, no mystery beans' }
        ]
    },
    benefits: {
        type: Array,
        default: () => [
            { boldText: 'Bulk orders ship FREE', text: 'we save on packaging and freight, and pass every dollar to you.' },
            { boldText: 'Better for the planet', text: 'fewer shipments = lower emissions + every bundle plants trees.' },
            { boldText: 'FAST ACTION BONUSES', text: 'Free $10 or $20 gift card + complete 5-Minute Morning Ritual Package with every 3- or 6-bag bundle.' }
        ],
    },
    showTopButton: {
        type: Boolean,
        default: true,
    },
    topButtonText: {
        type: String,
        default: 'Add to Cart',
    },
    topButtonLink: {
        type: String,
        default: 'the-products',
    },
});

defineBuilderComponent({
    friendlyName: 'Promotional Offer Hero Product',
    inputs: [
        {
            name: 'backgroundImage',
            type: 'file',
            friendlyName: 'Background Image',
            allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp', 'svg'],
            defaultValue: '/assets/images/bogo-new-header-2.jpg',
        },
        {
            name: 'mobileBackgroundImage',
            type: 'file',
            friendlyName: 'Mobile Background Image',
            allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp', 'svg'],
            defaultValue: '/assets/images/bogo-new-mobile-crop-new.jpg',
        },
        {
            name: 'backgroundImageAlt',
            type: 'text',
            friendlyName: 'Background Image Alt Text',
            defaultValue: 'Promotional offer background',
        },
        {
            name: 'productImage',
            type: 'file',
            friendlyName: 'Product Image',
            allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp', 'png', 'svg'],
            defaultValue: '/assets/images/bogo-new-6.png',
        },
        {
            name: 'productImageAlt',
            type: 'text',
            friendlyName: 'Product Image Alt Text',
            defaultValue: 'Promotional Offer Products',
        },
        {
            name: 'showTopButton',
            type: 'boolean',
            friendlyName: 'Show Top Button',
            defaultValue: true,
        },
        {
            name: 'topButtonText',
            type: 'text',
            friendlyName: 'Top Button Text',
            defaultValue: 'Add to Cart',
        },
        {
            name: 'topButtonLink',
            type: 'text',
            friendlyName: 'Top Button Link',
            defaultValue: '#offer',
        },
        {
            name: 'bannerText',
            type: 'richText',
            friendlyName: 'Banner Text',
            defaultValue: 'BUY 2, GET 1 FREE... BUY 4, GET 2 FREE ( 58% OFF )',
        },
        {
            name: 'mainHeadline',
            type: 'richText',
            friendlyName: 'Main Headline',
            defaultValue: 'Stock Up on Low Acid Coffee That\'s Easy On Your Stomach - Third-Party Verified: Mycotoxins Tested & Cleared',
        },
        {
            name: 'mobileMainHeadline',
            type: 'richText',
            friendlyName: 'Mobile Main Headline',
            defaultValue: '',
        },
        {
            name: 'subHeadline',
            type: 'richText',
            friendlyName: 'Sub Headline',
            defaultValue: 'For anyone who loves coffee but hates how it makes their stomach feel — or worries what\'s hiding in their beans — this is the cleanest, simplest upgrade you can make.',
        },
        {
            name: 'ctaText',
            type: 'text',
            friendlyName: 'CTA Button Text',
            defaultValue: 'ADD TO CART',
        },
        {
            name: 'ctaLink',
            type: 'text',
            friendlyName: 'CTA Button Link',
            defaultValue: '#offer',
        },
        {
            name: 'infoText',
            type: 'richText',
            friendlyName: 'Information Text',
            defaultValue: '<p>Every Lifeboost bag is single-origin, organic, naturally low-acid, and screened for 450+ unwanted compounds so you can enjoy your daily cup without second-guessing what\'s in it.</p><p>Buying in bulk saves you money, reduces packaging waste, cuts shipping emissions, and keeps your clean coffee shelf stocked for weeks.</p>',
        },
        {
            name: 'featuresTitle',
            type: 'richText',
            friendlyName: 'Features Title',
            defaultValue: 'Why People Switch to Lifeboost<br>(and Stay for Good):',
        },
        {
            name: 'features',
            type: 'list',
            friendlyName: 'Features (Icons in row)',
            subFields: [
                {
                    name: 'icon',
                    type: 'file',
                    friendlyName: 'Icon Image',
                    allowedFileTypes: ['svg', 'png', 'webp', 'jpg', 'jpeg'],
                },
                {
                    name: 'title',
                    type: 'text',
                    friendlyName: 'Title',
                },
                {
                    name: 'description',
                    type: 'text',
                    friendlyName: 'Description',
                }
            ],
            defaultValue: [
                { icon: '/assets/images/feature-low-acid.svg', title: 'Naturally low-acid & easy to drink', description: 'Gentle on taste, teeth, and sensitive stomachs.' },
                { icon: '/assets/images/feature-chemical.svg', title: 'Chemical-conscious sourcing', description: 'Organic farming, no pesticides, no flavor additives.' },
                { icon: '/assets/images/feature-toxins.svg', title: '450+ toxins tested (Including Mycotoxins) on every batch', description: 'Independent screening for purity and safety.' },
                { icon: '/assets/images/feature-single-origin.svg', title: 'Single-origin', description: 'No blends, no mystery beans' }
            ]
        },
        {
            name: 'benefits',
            type: 'list',
            friendlyName: 'Benefits List (Checkmarks)',
            subFields: [
                {
                    name: 'boldText',
                    type: 'text',
                    friendlyName: 'Bold Text',
                    defaultValue: 'Benefit Title',
                },
                {
                    name: 'text',
                    type: 'richText',
                    friendlyName: 'Description',
                    defaultValue: 'Benefit description.',
                },
            ],
            defaultValue: [
                { boldText: 'Bulk orders ship FREE', text: 'we save on packaging and freight, and pass every dollar to you.' },
                { boldText: 'Better for the planet', text: 'fewer shipments = lower emissions + every bundle plants trees.' },
                { boldText: 'FAST ACTION BONUSES', text: 'Free $10 or $20 gift card + complete 5-Minute Morning Ritual Package with every 3- or 6-bag bundle.' }
            ],
        },
    ],
});
</script>